import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Camera, Upload, Layers, Square, Sparkles as SparkleIcon, RotateCcw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import Pokeball from "@/components/Pokeball";
import Sparkles from "@/components/Sparkles";
import ResultsView from "@/components/ResultsView";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Phases: boot (closed, first load) -> open (UI) -> processing (closed spin) -> result-open (results)
export default function Home() {
  const [phase, setPhase] = useState("idle-closed");
  const [mode, setMode] = useState("single"); // "single" | "multi"
  const [imageData, setImageData] = useState(null); // { base64, previewUrl }
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [hintName, setHintName] = useState("");
  const [hintSet, setHintSet] = useState("");
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // On first load: full cinematic entrance (~1s) + a beat of stillness, THEN open.
  useEffect(() => {
    const t = setTimeout(() => setPhase("open"), 1600);
    return () => clearTimeout(t);
  }, []);

  const onFile = (file) => {
    if (!file) return;
    if (!/^image\/(jpeg|png|webp|jpg)$/i.test(file.type)) {
      toast.error("Please use a JPEG, PNG, or WEBP image.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      const base64 = dataUrl.split(",")[1];
      setImageData({ base64, previewUrl: dataUrl });
    };
    reader.readAsDataURL(file);
  };

  const runScan = async (overrides = null) => {
    if (!imageData) {
      toast.error("Take or upload a photo first.");
      return;
    }
    setError(null);
    setResult(null);
    setPhase("processing");
    try {
      const { data } = await axios.post(
        `${API}/scan`,
        {
          image_base64: imageData.base64,
          mode,
          hint_name: hintName.trim() || undefined,
          hint_set: hintSet.trim() || undefined,
          override_set_code: overrides?.set_code || undefined,
          override_card_number: overrides?.card_number || undefined,
          override_language: overrides?.language || undefined,
        },
        { timeout: 90_000 },
      );
      setResult(data);
      // Let the ball spin at least 1.2s for drama
      setTimeout(() => setPhase("result-open"), 1100);
    } catch (e) {
      // Distinguish network/timeout failures from backend HTTP errors so the
      // user gets actionable guidance instead of a generic "Network Error".
      let msg;
      if (e?.response) {
        // Backend responded with a non-2xx — show its detail.
        msg = e.response.data?.detail || `Server error (${e.response.status}).`;
      } else if (e?.code === "ECONNABORTED" || /timeout/i.test(e?.message || "")) {
        msg =
          "Scan timed out. Valuation pipelines can take up to 90s on slow networks — please try again on a better connection, or retake a tighter photo of the bottom edge so the fast-path can fire.";
      } else if (e?.message === "Network Error") {
        msg =
          "Couldn't reach the scanner server. Check your connection and try again. If it keeps failing, refresh the page.";
      } else {
        msg = e?.message || "Scan failed.";
      }
      // Also log full axios error to the browser console for debugging —
      // shows CORS blocks, DNS failures, and ingress timeouts explicitly.
      // eslint-disable-next-line no-console
      console.error("[scan] request failed", {
        message: e?.message,
        code: e?.code,
        status: e?.response?.status,
        data: e?.response?.data,
      });
      setError(msg);
      toast.error(msg);
      setPhase("open");
    }
  };

  const reset = () => {
    setResult(null);
    setError(null);
    setImageData(null);
    setHintName("");
    setHintSet("");
    setPhase("open");
  };

  // Re-open the camera with explicit footer-only guidance. When the current scan
  // came back "uncertain", capturing just the footer (set code + number) is the
  // fastest path to a confident identification.
  const rescanFooter = () => {
    reset();
    toast.info("Tip: focus on the BOTTOM edge of the card — capture the set code & card number sharply.", {
      duration: 5500,
    });
    // Slight delay so the reset/UI settles before triggering the native picker
    setTimeout(() => cameraInputRef.current?.click(), 150);
  };

  return (
    <div className="relative w-full min-h-screen overflow-hidden">
      {/* Top bar */}
      <header className="fixed top-0 left-0 right-0 z-30 px-6 sm:px-10 py-5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span
            className="inline-block w-2 h-2 rounded-full"
            style={{ background: "#E63946", boxShadow: "0 0 14px #E63946" }}
          />
          <span className="text-sm tracking-[0.22em] font-medium text-white/90">
            CARDSNAP · VALUE
          </span>
        </div>
        <div className="hidden sm:flex items-center gap-2 text-xs text-white/50 tracking-wider">
          <SparkleIcon className="w-3.5 h-3.5 gold" />
          <span>Real-time Pokémon card scanner</span>
        </div>
      </header>

      {/* Ambient animated sparkles — always visible */}
      <Sparkles />

      {/* 3D Pokeball canvas — used for closed intro, processing, results.
          When phase === "open", it blooms (scales up) while fading so the swap
          into the reference-image resting state feels like one continuous motion.
          Always pointer-events-none so it never blocks the 2D hit-zones or UI. */}
      <div
        className="fixed inset-0 z-0 transition-all duration-700 ease-out pointer-events-none"
        style={{
          opacity: phase === "open" ? 0 : 1,
          transform: phase === "open" ? "scale(1.22)" : "scale(1)",
          transformOrigin: "center 46%",
        }}
      >
        <Pokeball phase={phase} />
      </div>

      {/* Open endpoint — reference Pokéball image with invisible hit-zones.
          Sized by height (clamp 380-640, ~70vh) so the ball is a strong, large
          hero on mobile. Width follows the image aspect and is allowed to
          overflow narrow viewports — the radial mask naturally fades the edges
          into the dark background so overflow looks intentional. */}
      <AnimatePresence>
        {phase === "open" && (
          <motion.div
            key="openball-img"
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
            className="fixed inset-x-0 z-10 flex justify-center pointer-events-none top-[165px] sm:top-[225px] lg:top-[280px]"
          >
            <div
              className="relative max-h-[calc(100dvh-210px)] sm:max-h-[calc(100dvh-260px)] lg:max-h-[calc(100dvh-330px)]"
              style={{
                height: "clamp(380px, 78vh, 680px)",
                aspectRatio: "1448 / 1086",
              }}
            >
              <img
                src="/pokeball-open.png"
                alt=""
                className="absolute inset-0 w-full h-full object-contain select-none"
                draggable={false}
                style={{
                  WebkitMaskImage:
                    "radial-gradient(ellipse 48% 55% at 50% 58%, rgba(0,0,0,1) 35%, rgba(0,0,0,0.75) 55%, rgba(0,0,0,0.25) 80%, rgba(0,0,0,0) 100%)",
                  maskImage:
                    "radial-gradient(ellipse 48% 55% at 50% 58%, rgba(0,0,0,1) 35%, rgba(0,0,0,0.75) 55%, rgba(0,0,0,0.25) 80%, rgba(0,0,0,0) 100%)",
                }}
              />
              {!imageData && (
                <>
                  {/* SELECTED-STATE GLOW — a soft amber radial gradient that
                      slides between the Single and Multi halves. No hard border,
                      so it never looks misaligned with the drawn pill. */}
                  <div
                    aria-hidden="true"
                    className="absolute pointer-events-none transition-all duration-300 ease-out"
                    style={{
                      left: mode === "single" ? "28.5%" : "49.8%",
                      top: "58.5%",
                      width: "22%",
                      height: "5.5%",
                      background:
                        "radial-gradient(ellipse at center, rgba(251,191,36,0.55) 0%, rgba(251,191,36,0.28) 40%, rgba(251,191,36,0) 80%)",
                      mixBlendMode: "screen",
                    }}
                  />

                  {/* Single — left half of the pill */}
                  <button
                    type="button"
                    data-testid="mode-single-btn"
                    onClick={() => setMode("single")}
                    aria-pressed={mode === "single"}
                    aria-label="Single card mode"
                    className="absolute pointer-events-auto cursor-pointer rounded-full focus:outline-none"
                    style={{
                      left: "28.5%",
                      top: "58.5%",
                      width: "22%",
                      height: "5.5%",
                      background: "transparent",
                    }}
                  />
                  {/* Multi — right half of the pill */}
                  <button
                    type="button"
                    data-testid="mode-multi-btn"
                    onClick={() => setMode("multi")}
                    aria-pressed={mode === "multi"}
                    aria-label="Multi card mode"
                    className="absolute pointer-events-auto cursor-pointer rounded-full focus:outline-none"
                    style={{
                      left: "49.8%",
                      top: "58.5%",
                      width: "23%",
                      height: "5.5%",
                      background: "transparent",
                    }}
                  />

                  {/* Take Photo */}
                  <button
                    type="button"
                    data-testid="take-photo-btn"
                    onClick={() => cameraInputRef.current?.click()}
                    aria-label="Take photo"
                    className="absolute pointer-events-auto cursor-pointer rounded-xl focus:outline-none"
                    style={{
                      left: "30%",
                      top: "69.5%",
                      width: "20%",
                      height: "7.5%",
                      background: "transparent",
                    }}
                  />
                  {/* Upload Photo */}
                  <button
                    type="button"
                    data-testid="upload-photo-btn"
                    onClick={() => fileInputRef.current?.click()}
                    aria-label="Upload photo"
                    className="absolute pointer-events-auto cursor-pointer rounded-xl focus:outline-none"
                    style={{
                      left: "50.5%",
                      top: "69.5%",
                      width: "20%",
                      height: "7.5%",
                      background: "transparent",
                    }}
                  />
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hidden file inputs (shared by the image-based UI and the preview/analyze panel) */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => onFile(e.target.files?.[0])}
        data-testid="file-input"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => onFile(e.target.files?.[0])}
        data-testid="camera-input"
      />

      {/* Tagline — visible during the closed intro AND the open scan state. */}
      <AnimatePresence>
        {(phase === "idle-closed" || phase === "open") && !imageData && (
          <motion.div
            key="tagline"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="fixed top-16 sm:top-24 left-0 right-0 z-10 text-center px-5 pointer-events-none"
          >
            <h1 className="text-[28px] leading-[1.05] sm:text-5xl lg:text-6xl font-light tracking-tighter text-white text-balance">
              Scan any Pokémon card.
              <span className="block gold font-normal">See its real value.</span>
            </h1>
            <p className="mt-2 sm:mt-3 text-[11px] sm:text-sm text-white/50 tracking-wide px-4">
              Raw or graded · English or Japanese · Single or multi-card
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Preview + Analyze UI — appears once the user has picked or taken a photo.
          NOTE: The outer wrapper handles fixed positioning + flex-centering so the
          panel is always perfectly centered. Framer Motion's transform animates
          only scale/y on the inner node — never conflicts with centering. */}
      <AnimatePresence mode="wait">
        {phase === "open" && imageData && (
          <div
            key="preview-ui-wrap"
            className="fixed inset-0 z-20 flex items-center justify-center px-4 sm:px-6 py-20 overflow-y-auto pointer-events-none"
          >
            {/* soft scrim so the panel never competes visually with the ball behind */}
            <div
              aria-hidden="true"
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(ellipse 70% 55% at 50% 50%, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.55) 45%, rgba(0,0,0,0) 85%)",
              }}
            />
            <motion.div
              initial={{ opacity: 0, y: 18, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.45 }}
              className="relative w-full max-w-[min(92vw,28rem)] pointer-events-auto"
            >
              <PreviewPanel
                mode={mode}
                imageData={imageData}
                onAnalyze={runScan}
                onClear={() => setImageData(null)}
                hintName={hintName}
                setHintName={setHintName}
                hintSet={hintSet}
                setHintSet={setHintSet}
              />
            </motion.div>
          </div>
        )}

        {phase === "processing" && (
          <motion.div
            key="processing"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-x-0 bottom-0 z-20 flex justify-center pb-16 px-5 pointer-events-none"
          >
            <div className="glass rounded-full px-6 py-3 flex items-center gap-3">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-400" />
              </span>
              <span className="text-sm tracking-wider text-white/85">
                Analyzing card · valuing against live markets
              </span>
            </div>
          </motion.div>
        )}

        {phase === "result-open" && result && (
          <motion.div
            key="results"
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
            className="fixed inset-0 z-20 overflow-y-auto"
          >
            <ResultsView
              result={result}
              onRescan={reset}
              onRescanFooter={rescanFooter}
              onCorrectAnchor={(overrides) => runScan(overrides)}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function PreviewPanel({ mode, imageData, onAnalyze, onClear, hintName, setHintName, hintSet, setHintSet }) {
  return (
    <div
      data-testid="scan-panel"
      className="rounded-3xl px-4 sm:px-6 py-4 sm:py-5 w-full"
      style={{
        background: "rgba(10,10,12,0.92)",
        backdropFilter: "blur(18px)",
        border: "1px solid rgba(251, 191, 36, 0.35)",
        boxShadow:
          "0 0 30px rgba(251,191,36,0.18), inset 0 0 22px rgba(251,191,36,0.08), 0 20px 45px rgba(0,0,0,0.6)",
      }}
    >
      <div className="flex flex-col items-center gap-4">
        <div className="relative rounded-2xl overflow-hidden border border-amber-300/30 bg-black/70 w-full flex justify-center">
          <img
            src={imageData.previewUrl}
            alt="Card preview"
            className="max-h-40 sm:max-h-44 w-auto object-contain"
            data-testid="image-preview"
          />
        </div>

        <div className="w-full space-y-2">
          <p className="text-[10px] tracking-[0.18em] text-amber-200/60 text-center">
            OPTIONAL · HELP US FIND THE EXACT CARD
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              type="text"
              value={hintName}
              onChange={(e) => setHintName(e.target.value)}
              placeholder="Card name"
              data-testid="hint-name-input"
              className="w-full px-3 py-2 rounded-xl bg-black/70 border border-amber-300/25 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-amber-300/70 focus:ring-1 focus:ring-amber-300/40 transition"
            />
            <input
              type="text"
              value={hintSet}
              onChange={(e) => setHintSet(e.target.value)}
              placeholder="Series / set"
              data-testid="hint-set-input"
              className="w-full px-3 py-2 rounded-xl bg-black/70 border border-amber-300/25 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-amber-300/70 focus:ring-1 focus:ring-amber-300/40 transition"
            />
          </div>
        </div>

        <div className="flex items-center justify-center gap-2 sm:gap-3 pt-1 w-full">
          <button
            type="button"
            className="btn-ghost text-sm flex-1 sm:flex-none"
            onClick={onClear}
            data-testid="clear-image-btn"
          >
            Replace
          </button>
          <button
            type="button"
            className="btn-primary text-sm flex-1 sm:flex-none"
            onClick={onAnalyze}
            data-testid="analyze-btn"
          >
            Analyze Card{mode === "multi" ? "s" : ""}
          </button>
        </div>
      </div>
    </div>
  );
}

